import type { Problem } from './types';

/**
 * RFC 9457 `application/problem+json` mapped to a typed error. The server is
 * authoritative; the frontend surfaces the stable machine `code` without
 * inventing its own error taxonomy (specification §4.3).
 */
export class ApiError extends Error {
  readonly problem: Problem;
  readonly status: number;

  constructor(problem: Problem, status: number) {
    super(problem.detail ?? problem.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.problem = problem;
    this.status = status;
  }
}

/** Minimal structural guard for a RFC 9457 problem body. */
export function isProblem(value: unknown): value is Problem {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<Problem>;
  return (
    typeof candidate.title === 'string' &&
    typeof candidate.code === 'string' &&
    typeof candidate.status === 'number'
  );
}

/** Coerce an arbitrary error body into a Problem-shaped object for display. */
export function toProblem(value: unknown, fallbackStatus: number): Problem {
  if (isProblem(value)) return value;
  return {
    type: 'about:blank',
    title: 'Request failed',
    status: fallbackStatus,
    code: 'REQUEST_FAILED',
    detail: typeof value === 'string' ? value : '',
    instance: '',
    requestId: '',
  };
}
